import { HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { NgxHttpClient } from '@app/shared/ngx-http-client';
import { WebEnvironmentService } from '@dg/shared-services';
import { catchError, map, Observable, of, shareReplay, Subject } from 'rxjs';
import {
  Coach,
  CoachConversation,
  CoachConversationResponse,
  CoachFeedbackOption,
  CoachInference,
  CoachRecommendationResponse,
  DGAssistantDefaultPrompt,
  Feedback,
  SkillAssessment,
  Summary,
  UploadResponse,
} from '../models/coach.model';
import { COACH_API } from '../shared/constants';
import { extractSkillReport, replaceValues } from '../shared/helpers';

@Injectable()
export class CoachAPIService {
  private dataService = inject(NgxHttpClient);
  private summaySubject = new Subject<{
    summaries: Summary[];
    skillReport: SkillAssessment;
  }>();
  private webEnvironmentService = inject(WebEnvironmentService);
  public get fallBackAvatar(): string {
    return this.webEnvironmentService.getBlobUrl(
      '/content/img/branding/degreed-logo-glasses.svg',
      true
    );
  }
  public summary$ = this.summaySubject.asObservable();
  private feedbackOptions$: Observable<CoachFeedbackOption[]>;

  public createNewConversation(payload?: {
    coachId: number;
    pathwayId?: number;
  }): Observable<CoachConversation> {
    return this.dataService.post(COACH_API.CREATE_CONVERSATION, payload);
  }

  public deleteConversation(conversationId: number): Observable<void> {
    return this.dataService.delete(
      `${COACH_API.DELETE_CONVERSATION}/${conversationId}`
    );
  }

  public exportSummaryAsPDF(
    conversationId: number
  ): Observable<HttpResponse<Blob>> {
    return this.dataService.get(
      `${COACH_API.SUMMARIES}/${conversationId}/pdf`,
      {
        observe: 'response',
        responseType: 'blob',
      }
    );
  }

  public getCoachById(coachId: string | number): Observable<Coach> {
    return this.dataService.get(`${COACH_API.GET_COACH}/${coachId}`);
  }

  public getCoachList(params?: { searchTitle?: string }): Observable<Coach[]> {
    return this.dataService.get(COACH_API.COACH_LIST, { params }).pipe(
      map((coaches: Coach[]) =>
        coaches?.filter((c: Coach) => c.privacyId === 1)
      ),
      catchError((error: Error) => {
        console.error('Error fetching coach list:', error);
        return of([]);
      })
    );
  }

  public getCoachListForAdmin(): Observable<Coach[]> {
    return this.dataService.get(COACH_API.ADMIN_COACH_LIST);
  }

  public getCoachForAdmin(coachId: string): Observable<Coach> {
    return this.dataService.get(`${COACH_API.GET_COACH_FOR_ADMIN}/${coachId}`);
  }

  public getConversationById(
    conversationId: number,
    coachId: number
  ): Observable<CoachConversation> {
    const url = replaceValues(COACH_API.GET_CONVERSATION_BY_ID, {
      coachId: coachId.toString(),
      conversationId: conversationId.toString(),
    });
    return this.dataService.get(url);
  }

  public getConversations(params: {
    coachId?: string;
    page?: number;
    pageSize?: number;
  }): Observable<CoachConversationResponse> {
    return this.dataService.get(`${COACH_API.GET_CONVERSATIONS}`, { params });
  }

  public getMasterCoach(): Observable<Coach> {
    return this.dataService
      .get(COACH_API.COACH_LIST, { params: { coachType: 'Master' } })
      .pipe(
        map((coaches: Coach[]) => coaches?.[0]),
        catchError((err: any) => of(err))
      );
  }

  public getFeedbackOptions(): Observable<CoachFeedbackOption[]> {
    if (!this.feedbackOptions$) {
      this.feedbackOptions$ = this.dataService
        .get<CoachFeedbackOption[]>(COACH_API.GET_FEEDBACK_OPTIONS)
        .pipe(shareReplay(1));
    }
    return this.feedbackOptions$;
  }

  public getRecommendations(
    coachId?: number,
    conversationId?: number
  ): Observable<CoachRecommendationResponse> {
    return this.dataService.get(
      `${COACH_API.RECOMMENDATION}/${conversationId}`,
      {
        params: { coachId },
      }
    );
  }

  public getSkillReport(conversationId: number): Observable<CoachInference> {
    return this.dataService.get(COACH_API.SKILL_REPORT, {
      params: { conversationId },
    });
  }

  public getTopNConversation(
    coachId: string,
    topN: number = 4
  ): Observable<CoachConversationResponse> {
    return this.dataService.get(COACH_API.GET_CONVERSATIONS, {
      params: { topN, coachId },
    });
  }

  public getDGAFlows(): Observable<DGAssistantDefaultPrompt[]> {
    return this.dataService.get('/assistant/flows');
  }

  public sendFeedback(payload: {
    messageId: number;
    feedback: string;
    feedbackText: string;
  }): Observable<Feedback> {
    return this.dataService.post(COACH_API.FEEDBACK, payload);
  }

  public setSummary(conversation: CoachConversation): void {
    this.summaySubject.next({
      summaries: conversation.summaries,
      skillReport: extractSkillReport(conversation),
    });
  }

  public updateConversation(payload: {
    conversationId: number;
    coachId: number;
    endedAt: string;
  }): Observable<CoachConversation> {
    return this.dataService.put(`${COACH_API.UPDATE_CONVERSATION}`, payload);
  }

  public updatePartialConversation(payload: {
    conversationId: number;
    coachId: number;
    endedAt: string;
  }): Observable<CoachConversation> {
    return this.dataService.put(`${COACH_API.UPDATE_PARTIAL}`, payload);
  }

  public generatePrompts(payload: {
    coachName: string;
    coachDescription: string;
    persona: string;
    role: string;
    numberOfPrompts: number;
  }): Observable<any> {
    return this.dataService.post(COACH_API.CONVERSATION_STARTERS, payload);
  }

  public createDraftCoach(): Observable<string> {
    return this.dataService.post(COACH_API.CREATE_DRAFT, {});
  }

  public createCoach(coach: Coach): Observable<Coach> {
    return this.dataService.post(COACH_API.CREATE, coach);
  }

  public updateCoach(coach: Coach): Observable<Coach> {
    return this.dataService.put(COACH_API.UPDATE, coach);
  }

  public deleteCoach(coachId: number): Observable<void> {
    return this.dataService.delete(`${COACH_API.DELETE}/${coachId}`);
  }

  public uploadFiles(
    coachId: string,
    formData: FormData
  ): Observable<UploadResponse> {
    return this.dataService.post(
      `${COACH_API.UPLOAD_FILES}/${coachId}`,
      formData
    );
  }

  public deleteFile({
    knowledgeBaseId,
    docIds,
    coachId,
  }: {
    knowledgeBaseId: string;
    docIds: string[];
    coachId: string;
  }): Observable<void> {
    return this.dataService.delete(`${COACH_API.DELETE_FILES}`, {
      body: {
        knowledgeBaseId,
        docIds,
        coachId,
      },
    });
  }

  public buildCoach(payload: { coachDescription: string }): Observable<Coach> {
    return this.dataService.post(`${COACH_API.GENERATATE_COACH}`, payload);
  }
}
